import { WORKSPACE_FARBE_VORGABE, WORKSPACE_FARBEN, type WorkspaceFarbe } from '@netcon-work/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, ChevronsUpDown, Plus, Trash2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { cn } from '../lib/cn'
import { workspacesQuery } from '../lib/queries'
import { aktiverWorkspaceIst, trpc, workspaceSetzen } from '../lib/trpc'
import { WELT_SPEICHER_SCHLUESSEL, weltWechseln } from '../lib/welt'
import { FarbWahl, WorkspacePunkt, farbklassen } from './WorkspaceMarke'
import { Button } from './ui/primitives'

/**
 * **Der Workspace-Umschalter** ([Z-07](../../../../docs/phasen/zusatz-07-workspaces.md)).
 *
 * Nils, 25.08.2026: *„Wahrscheinlich auch einen Workspace-Umschalter im Menü, um nicht in
 * jeder Seite Workspaces umzuschalten oder? Bzw. Außer Mein Tag, das vielleicht separat und
 * darunter dann die Workspaces auswahl + die andern Punkte?"*
 *
 * ## Warum er im Menü steht und nicht oben rechts
 *
 * Weil er der **Kopf des gebundenen Blocks** ist. Steht er frei in einer Kopfleiste, liest man
 * „irgendwo ist ein Filter aktiv"; steht er zwischen „Mein Tag" und „Eingang", liest man
 * „diese Punkte darunter gehören zu dieser Arbeitswelt". Das ist dieselbe Aussage in anderer
 * Anordnung – und die Anordnung ist hier die halbe Erklärung.
 *
 * ## Warum er bei einem einzigen Workspace verschwindet
 *
 * Ein Umschalter ohne Wahl ist keine Bedienung, sondern eine Behauptung: er sagt „hier gibt es
 * mehrere", wo es eine gibt. Solange „Entwicklung" allein dasteht – also heute, direkt nach
 * der Migration –, sieht die Seitenleiste deshalb genau aus wie vorher. Das ist gewollt: der
 * Umbau soll erst unsichtbar sein und dann sichtbar werden.
 *
 * Ein **Gast** bekommt eine leere Liste (er ist nirgends Mitglied) und damit ebenfalls nichts.
 */

const SPEICHER_SCHLUESSEL = WELT_SPEICHER_SCHLUESSEL

/**
 * **Was zuletzt gewählt war** – aus dem Browserspeicher, vor dem ersten Zeichnen.
 *
 * Läuft als Modulcode und nicht in einem Effekt: die Kopfzeile wird beim **allerersten**
 * Abruf gebraucht, und ein Effekt liefe erst danach. Ohne das führe jeder Seitenaufruf
 * zunächst im ersten Workspace, um eine Sekunde später umzuspringen – sichtbar, und bei
 * langsamen Abfragen zweimal geladen.
 *
 * Der gespeicherte Wert ist ungeprüft; das macht nichts. Der Server nimmt ihn nicht an, wenn
 * er nicht zur Mitgliedschaft passt – dann greift {@link WorkspaceUmschalter}s Abgleich unten
 * und setzt ihn zurück.
 */
try {
  const gemerkt = globalThis.localStorage?.getItem(SPEICHER_SCHLUESSEL)
  if (gemerkt) workspaceSetzen(gemerkt)
} catch {
  /* Kein Speicher (privater Modus, Test) – dann eben ohne Gedächtnis. */
}

export function WorkspaceUmschalter() {
  const queryClient = useQueryClient()
  const { data: workspaces } = useQuery(workspacesQuery)
  const { data: lage } = useQuery({
    queryKey: ['workspaces', 'aktiv'],
    queryFn: () => trpc.workspaces.aktiv.query(),
  })
  const darfAnlegen = lage?.darfAnlegen ?? false
  const darfGeteilt = lage?.darfGeteiltAnlegen ?? false
  const [offen, setOffen] = useState(false)
  const [aktiv, setAktiv] = useState<string | null>(() => aktiverWorkspaceIst())
  const huelle = useRef<HTMLDivElement>(null)

  /**
   * **Ein gemerkter Workspace, den es nicht mehr gibt, muss weg.**
   *
   * Der Fall ist echt: der Admin nimmt jemanden aus „Marketing" heraus, und im Browser steht
   * die Kennung weiter. Jede Anfrage bekäme dann `NOT_FOUND` – die Anwendung wäre kaputt, und
   * zwar auf eine Art, die kein Neuladen behebt. Deshalb wird hier gegen die Liste geprüft und
   * im Zweifel auf den ersten eigenen zurückgefallen.
   */
  useEffect(() => {
    if (!workspaces || workspaces.length === 0) return
    const gueltig = aktiv && workspaces.some((w) => w.id === aktiv)
    if (gueltig) return
    const erster = workspaces[0]?.id ?? null
    setAktiv(erster)
    workspaceSetzen(erster)
    if (erster) {
      try {
        globalThis.localStorage?.setItem(SPEICHER_SCHLUESSEL, erster)
      } catch {
        /* siehe oben */
      }
    }
  }, [workspaces, aktiv])

  /** Klick nach draußen schließt – wie bei jeder Auswahlliste. */
  useEffect(() => {
    if (!offen) return
    const zu = (e: MouseEvent) => {
      if (!huelle.current?.contains(e.target as Node)) setOffen(false)
    }
    document.addEventListener('mousedown', zu)
    return () => document.removeEventListener('mousedown', zu)
  }, [offen])

  /**
   * **Unter zwei Einträgen kein Umschalter – aber ein Anlegen-Knopf, wenn es einer sein darf.**
   *
   * Ein Umschalter ohne Wahl ist keine Bedienung, sondern eine Behauptung: er sagt „hier gibt es
   * mehrere", wo es eine gibt. Der Weg zum **eigenen** Workspace muss trotzdem irgendwo
   * anfangen, und die Seitenleiste ist der Ort, an dem man ihn sucht – nicht die
   * Administrationsseite, die ein Entwickler nie öffnet.
   */
  if (!workspaces) return null
  if (workspaces.length < 2)
    return darfAnlegen ? <WorkspaceAnlegen geteiltErlaubt={darfGeteilt} /> : null

  const jetzt = workspaces.find((w) => w.id === aktiv) ?? workspaces[0]
  if (!jetzt) return null

  function wechseln(id: string) {
    setOffen(false)
    if (id === aktiv) return
    setAktiv(id)
    /** Kopfzeile, Gedächtnis und Zwischenspeicher – siehe `weltWechseln` in `lib/welt.ts`. */
    weltWechseln(id, queryClient)
  }

  return (
    <div ref={huelle} className="relative mt-4 mb-1.5">
      <button
        type="button"
        onClick={() => setOffen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={offen}
        className={cn(
          'flex w-full items-center gap-2 rounded-[10px] border border-line px-3 py-2 text-left text-[13px] font-semibold',
          /**
           * **Der Umschalter trägt dieselbe Farbe wie die Marke in der Kopfzeile.**
           *
           * Sonst wären es zwei Anzeigen derselben Sache in zwei Anstrichen, und man müsste
           * beide lesen, um zu glauben, dass sie dasselbe meinen.
           */
          farbklassen(jetzt.farbe).flaeche,
          farbklassen(jetzt.farbe).text,
        )}
      >
        <span className="text-[15px] leading-none">{jetzt.icon || '📁'}</span>
        <span className="min-w-0 flex-1 truncate">{jetzt.label}</span>
        <ChevronsUpDown className="size-[15px] shrink-0 text-muted" />
      </button>

      {offen && (
        <ul
          role="listbox"
          className="absolute top-full right-0 left-0 z-20 mt-1 overflow-hidden rounded-[10px] border border-line bg-white py-1 shadow-lg"
        >
          {workspaces.map((w) => (
            <li key={w.id}>
              <button
                type="button"
                role="option"
                aria-selected={w.id === jetzt.id}
                onClick={() => wechseln(w.id)}
                className={cn(
                  'flex w-full items-center gap-2 px-3 py-[7px] text-left text-[13px]',
                  w.id === jetzt.id ? 'font-semibold text-accent-deep' : 'text-ink2',
                  'hover:bg-[#f3f2ef]',
                )}
              >
                <WorkspacePunkt farbe={w.farbe} />
                <span className="text-[15px] leading-none">{w.icon || '📁'}</span>
                <span className="min-w-0 flex-1 truncate">{w.label}</span>
                {/**
                 * **Das Mail-Kürzel steht dabei, wo man es sucht** (E-272, 28.08.2026).
                 *
                 * Ein Kürzel, das man nur in der Administration nachschlagen kann, tippt niemand
                 * in eine Betreffzeile – und dann gibt es die Funktion praktisch nicht. Hier
                 * steht es neben dem Namen, in derselben Schreibweise wie im Betreff.
                 */}
                {w.kuerzel && (
                  <span
                    className="shrink-0 font-mono text-[10.5px] text-muted"
                    title={`„#${w.kuerzel}" im Betreff einer Mail an Work schickt sie hierher`}
                  >
                    #{w.kuerzel}
                  </span>
                )}
                {/**
                 * Die Art steht **nur** bei persönlichen dabei. „Geteilt" an jedem zweiten
                 * Eintrag wäre eine Wiederholung; „Persönlich" ist die Ausnahme und deshalb
                 * die Angabe, die man sucht.
                 */}
                {w.art === 'persoenlich' && (
                  <span className="shrink-0 text-[10.5px] text-muted">persönlich</span>
                )}
                {w.id === jetzt.id && <Check className="size-[14px] shrink-0" />}
              </button>
            </li>
          ))}
          {/**
           * **Die Farbe des eigenen Workspace, nachträglich.**
           *
           * Nur bei persönlichen: die Farbe eines geteilten sehen acht Leute, und das ist keine
           * persönliche Vorliebe mehr – die setzt der Admin in der Verwaltung. Der Server sagt
           * dasselbe noch einmal (`aussehenSetzen` prüft den Besitzer); hier steht nur, was man
           * sinnvoll anbieten kann.
           */}
          {jetzt.art === 'persoenlich' && (
            <li className="flex items-center gap-2 border-t border-line px-3 py-2">
              <span className="text-[11px] text-muted">Farbe</span>
              <EigeneFarbe id={jetzt.id} farbe={jetzt.farbe} />
            </li>
          )}
          {/**
           * **Und wieder weg damit** ([E-328](../../../../docs/ENTSCHEIDUNGEN.md#e-328)).
           *
           * Steht hier und nicht in den Einstellungen, aus demselben Grund wie das Anlegen zwei
           * Zeilen weiter unten: die Seitenleiste ist der Ort, an dem man Workspaces sucht. Die
           * Administrationsseite öffnet ein Entwickler nie – und ein Workspace, den nur ein
           * Administrator wieder loswird, ist einer, den man lieber gar nicht erst anlegt.
           *
           * Nur beim **eigenen persönlichen**: `gehoertMir` sagt, wessen er ist – `art` sagt
           * das nicht ([E-278](../../../../docs/ENTSCHEIDUNGEN.md#e-278)). Der Server prüft
           * dasselbe noch einmal; hier steht nur, was man sinnvoll anbieten kann.
           */}
          {jetzt.art === 'persoenlich' && jetzt.gehoertMir && (
            <li className="border-t border-line">
              <EigenenLoeschen
                id={jetzt.id}
                label={jetzt.label}
                weiter={workspaces.find((w) => w.id !== jetzt.id)?.id ?? null}
                onFertig={() => setOffen(false)}
              />
            </li>
          )}
          {darfAnlegen && (
            <li className="border-t border-line">
              <WorkspaceAnlegen
                kompakt
                geteiltErlaubt={darfGeteilt}
                onFertig={() => setOffen(false)}
              />
            </li>
          )}
        </ul>
      )}
    </div>
  )
}

/**
 * **Sechs Punkte, die sofort wirken** – kein Speichern-Knopf.
 *
 * Eine Farbe ist keine Eingabe, die man prüft und bestätigt; man klickt sie an und sieht, ob sie
 * gefällt. Der optimistische Weg wäre hier trotzdem zu viel: es ist ein Klick alle paar Monate,
 * und die Marke oben soll erst umspringen, wenn sie wirklich umgesprungen ist.
 */
function EigeneFarbe({ id, farbe }: { id: string; farbe: string }) {
  const queryClient = useQueryClient()
  const setzen = useMutation({
    mutationFn: (neu: WorkspaceFarbe) => trpc.workspaces.aussehenSetzen.mutate({ id, farbe: neu }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['workspaces'] }),
  })
  const wert = (WORKSPACE_FARBEN as readonly string[]).includes(farbe)
    ? (farbe as WorkspaceFarbe)
    : WORKSPACE_FARBE_VORGABE
  return <FarbWahl wert={wert} onWahl={(f) => setzen.mutate(f)} />
}

/**
 * **Den eigenen Workspace wieder löschen** ([E-328](../../../../docs/ENTSCHEIDUNGEN.md#e-328),
 * 04.09.2026).
 *
 * Nils: *„Einen angelegten Workspace können die Leute selber nicht mehr löschen (den die selber
 * angelegt haben und der leer ist)."*
 *
 * ## Zwei Klicks, und der zweite steht woanders
 *
 * Kein `window.confirm`: das ist ein Systemdialog mitten in einer Seitenleiste und sagt außer
 * „OK/Abbrechen" nichts. Stattdessen verwandelt sich die Zeile – aus *„Diesen Workspace
 * löschen"* wird *„Wirklich löschen?"* mit **Ja** und **Nein**. Der bestätigende Klick trifft
 * damit eine andere Stelle als der erste; wer zweimal auf dieselbe Stelle tippt, löscht nichts.
 *
 * ## Der Fehler des Servers wird gezeigt, nicht übersetzt
 *
 * *„Hier liegen noch 12 Aufgaben. Erst leeren oder den Workspace deaktivieren."* ist die
 * brauchbare Antwort – sie nennt die Zahl. Eine eigene Formulierung hier wäre eine zweite
 * Wahrheit über dieselbe Regel, und die ginge irgendwann auseinander.
 */
function EigenenLoeschen({
  id,
  label,
  weiter,
  onFertig,
}: {
  id: string
  label: string
  /** Wohin danach – irgendein anderer, den diese Person hat; `null`, wenn es keinen gibt. */
  weiter: string | null
  onFertig: () => void
}) {
  const queryClient = useQueryClient()
  const [sicher, setSicher] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)

  const loeschen = useMutation({
    mutationFn: () => trpc.workspaces.loeschen.mutate({ id }),
    onSuccess: () => {
      setFehler(null)
      onFertig()
      /**
       * **Erst umschalten, dann aufräumen.** Bliebe die gelöschte Welt aktiv, liefe die
       * nächste Abfrage gegen eine Kennung, die es nicht mehr gibt – und der Server
       * beantwortet das zu Recht mit `NOT_FOUND`.
       */
      if (weiter) weltWechseln(weiter, queryClient)
      else void queryClient.invalidateQueries()
    },
    onError: (e: unknown) => setFehler(e instanceof Error ? e.message : 'Das ging nicht.'),
  })

  if (!sicher) {
    return (
      <button
        type="button"
        onClick={() => setSicher(true)}
        className="flex w-full items-center gap-2 px-3 py-[7px] text-left text-[12.5px] text-muted hover:bg-pool-sofort-bg hover:text-pool-sofort"
      >
        <Trash2 className="size-[14px] shrink-0" />
        Diesen Workspace löschen
      </button>
    )
  }

  return (
    <div className="px-3 py-2">
      <p className="text-[12px] text-ink2">„{label}" wirklich löschen?</p>
      <div className="mt-1.5 flex gap-1.5">
        <Button
          type="button"
          size="sm"
          className="bg-pool-sofort text-white"
          disabled={loeschen.isPending}
          onClick={() => loeschen.mutate()}
        >
          Ja, löschen
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => {
            setSicher(false)
            setFehler(null)
          }}
        >
          Nein
        </Button>
      </div>
      {fehler && <p className="mt-1.5 text-[11.5px] text-pool-sofort">{fehler}</p>}
    </div>
  )
}

/**
 * **Einen Workspace anlegen** – Z-07.12, erweitert am 25.08.2026.
 *
 * Nils: *„privat ist nicht mit «Privat» gemeint sondern eher «Ich». Es geht quasi um eigene
 * Todos die nicht geteilt sind."* Und beim Ausprobieren: *„Wenn ich Admin bin möchte ich den
 * Geteilten Workspace auch da anlegen können wo ich den privaten anlege."*
 *
 * ## Ein Einstieg, zwei Arten – und die Wahl steht **im** Formular
 *
 * Zwei Knöpfe nebeneinander („Eigener…" / „Geteilter…") wären der schnellere Weg gewesen und
 * der schlechtere: sie stehen in einer schmalen Seitenleiste, und der Unterschied zwischen
 * ihnen ist keine Kleinigkeit – der eine Workspace gehört dir allein, den anderen sehen
 * andere. Diese Frage gehört an die Stelle, an der man den Namen eintippt, nicht davor.
 *
 * Wer kein Admin ist, sieht die Wahl gar nicht: für ihn gibt es nur die eine Art, und eine
 * Auswahl mit einer Möglichkeit ist keine Auswahl.
 *
 * ## Warum es auch in der Seitenleiste steht und nicht nur in den Einstellungen
 *
 * Weil die Seitenleiste der Ort ist, an dem man Workspaces **sucht**. Die
 * Administrationsseite ist der Ort, an dem man sie **verwaltet** – Mitglieder, Auffangbecken,
 * Löschen. Zwei verschiedene Fragen, und die erste stellt man häufiger.
 */
function WorkspaceAnlegen({
  kompakt,
  geteiltErlaubt,
  onFertig,
}: {
  kompakt?: boolean
  geteiltErlaubt: boolean
  onFertig?: () => void
}) {
  const queryClient = useQueryClient()
  const [name, setName] = useState<string | null>(null)
  const [geteilt, setGeteilt] = useState(false)
  const [farbe, setFarbe] = useState<WorkspaceFarbe>(WORKSPACE_FARBE_VORGABE)
  const [fehler, setFehler] = useState<string | null>(null)

  const fertig = () => {
    setName(null)
    setGeteilt(false)
    setFarbe(WORKSPACE_FARBE_VORGABE)
    setFehler(null)
    onFertig?.()
    void queryClient.invalidateQueries()
  }
  const anlegen = useMutation({
    mutationFn: (label: string) =>
      geteilt
        ? trpc.workspaces.anlegen.mutate({ label, icon: '📁', farbe })
        : trpc.workspaces.persoenlichAnlegen.mutate({ label, icon: '👤', farbe }),
    onSuccess: fertig,
    onError: (e: unknown) => setFehler(e instanceof Error ? e.message : 'Das ging nicht.'),
  })

  if (name === null) {
    return (
      <button
        type="button"
        onClick={() => setName('')}
        className={cn(
          'flex w-full items-center gap-2 text-left text-[12.5px] font-semibold text-accent-deep hover:bg-accent-soft',
          kompakt ? 'px-3 py-[7px]' : 'mt-4 mb-1.5 rounded-[10px] px-3 py-2',
        )}
      >
        <Plus className="size-[14px] shrink-0" />
        Workspace anlegen
      </button>
    )
  }

  return (
    <div className={cn(kompakt ? 'px-2 py-2' : 'mt-4 mb-1.5')}>
      <input
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={geteilt ? 'Marketing' : 'Meine Todos'}
        aria-label="Name des Workspace"
        className="w-full rounded-lg border border-line px-2.5 py-1.5 text-[12.5px]"
        onKeyDown={(e) => {
          if (e.key === 'Enter' && name.trim().length >= 2) anlegen.mutate(name.trim())
          if (e.key === 'Escape') setName(null)
        }}
      />

      {geteiltErlaubt && (
        <div className="mt-1.5 flex gap-1">
          {[
            { wert: false, text: 'Nur für mich' },
            { wert: true, text: 'Geteilt' },
          ].map((w) => (
            <button
              key={String(w.wert)}
              type="button"
              onClick={() => setGeteilt(w.wert)}
              className={cn(
                'flex-1 rounded-md px-2 py-1 text-[11.5px] font-semibold',
                geteilt === w.wert
                  ? 'bg-accent-soft text-accent-deep'
                  : 'text-muted hover:bg-[#f3f2ef]',
              )}
            >
              {w.text}
            </button>
          ))}
        </div>
      )}

      <FarbWahl wert={farbe} onWahl={setFarbe} className="mt-1.5 px-0.5" />

      <p className="mt-1 px-0.5 text-[10.5px] leading-snug text-muted">
        {geteilt
          ? 'Andere können zugeteilt werden. Du bist von Anfang an dabei.'
          : 'Sieht niemand außer dir – auch die Administration nicht. Aufgaben darin zählen nicht in die Auslastung.'}
      </p>
      {fehler && <p className="mt-1 px-0.5 text-[10.5px] text-pool-sofort">{fehler}</p>}
    </div>
  )
}
